import type { Contact, CustomerOrder } from "../types";

const customerStatusLabels: Record<Contact["customer_status"], string> = {
  PROSPECT: "潜在客户",
  QUALIFYING: "评估中",
  ACTIVE: "重点推进中",
  PAUSED: "暂缓",
  UNSUITABLE: "不适合"
};

const orderStatusLabels: Record<CustomerOrder["status"], string> = {
  INQUIRY: "询盘",
  REQUIREMENT_CONFIRMED: "需求已确认",
  QUOTING: "报价中",
  SAMPLING: "打样中",
  SAMPLE_CONFIRMATION: "样品确认",
  DEPOSIT_PENDING: "待收定金",
  PRODUCTION: "生产中",
  QUALITY_CHECK: "质检中",
  BALANCE_PENDING: "待收尾款",
  SHIPPING: "发货中",
  COMPLETED: "已完成",
  PAUSED: "已暂停",
  CANCELLED: "已取消"
};

const headers = [
  "关注顺序",
  "客户姓名",
  "公司/品牌",
  "国家/市场",
  "联系渠道",
  "联系账号",
  "重要性",
  "客户状态",
  "主营产品",
  "采购季节",
  "年度采购预算",
  "客户背景/合作判断",
  "客户下一步",
  "客户下次跟进",
  "客户登记时间",
  "订单编号",
  "订单/款式",
  "订单状态",
  "金额",
  "币种",
  "数量（件）",
  "客户要求",
  "订单下一步",
  "订单下次跟进",
  "订单备注",
  "款式图片链接",
  "订单登记时间"
];

function csvCell(value: string | number | null | undefined) {
  const normalized = String(value ?? "").replace(/\r\n?/g, "\n");
  const formulaSafe = /^[=+\-@\t]/.test(normalized) ? `'${normalized}` : normalized;
  return `"${formulaSafe.replace(/"/g, '""')}"`;
}

function formatLocalDateTime(value: string | null | undefined, timeZone: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(date).replaceAll("/", "-");
  } catch {
    return new Intl.DateTimeFormat("zh-CN", {
      timeZone: "Asia/Shanghai",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(date).replaceAll("/", "-");
  }
}

function imageLinks(order: CustomerOrder) {
  try {
    const parsed = JSON.parse(order.images_json) as unknown;
    if (!Array.isArray(parsed)) return "";
    return parsed.filter((item): item is string => typeof item === "string").join("\n");
  } catch {
    return "";
  }
}

function makeRow(
  contact: Contact,
  order: CustomerOrder | undefined,
  rank: number,
  timeZone: string
) {
  return [
    rank,
    contact.name,
    contact.organization,
    contact.country,
    contact.contact_channel,
    contact.channel_account,
    contact.importance,
    customerStatusLabels[contact.customer_status],
    contact.main_products,
    contact.purchase_season,
    contact.annual_purchase,
    contact.context,
    contact.next_action,
    contact.next_followup,
    formatLocalDateTime(contact.created_at, timeZone),
    order?.order_code,
    order?.title,
    order ? orderStatusLabels[order.status] : "",
    order ? (order.amount_minor / 100).toFixed(2) : "",
    order?.currency,
    order?.quantity,
    order?.requirements,
    order?.next_action,
    order?.next_followup,
    order?.note,
    order ? imageLinks(order) : "",
    order ? formatLocalDateTime(order.created_at, timeZone) : ""
  ].map(csvCell).join(",");
}

export function buildCustomerExportCsv(
  contacts: Contact[],
  orders: CustomerOrder[],
  timeZone = "Asia/Shanghai"
) {
  const rows = contacts.flatMap((contact, index) => {
    const customerOrders = orders.filter((order) => order.contact_id === contact.id);
    if (!customerOrders.length) return [makeRow(contact, undefined, index + 1, timeZone)];
    return customerOrders.map((order) => makeRow(contact, order, index + 1, timeZone));
  });
  return `\uFEFF${[headers.map(csvCell).join(","), ...rows].join("\r\n")}`;
}

function dateKey(date: Date, timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(date);
    const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${value.year}-${value.month}-${value.day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function downloadCustomerExport(
  contacts: Contact[],
  orders: CustomerOrder[],
  timeZone = "Asia/Shanghai"
) {
  const csv = buildCustomerExportCsv(contacts, orders, timeZone);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `地球Online_客户与订单_${dateKey(new Date(), timeZone)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
